import { getLocalizedText } from "@salon/types";
import { MoreVertical, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSalonStore } from "@/lib/store/salon";

interface TemplateOption {
  id: string;
  optionName: Record<string, string> | string;
  durationModifier: number;
  isDefault: boolean;
}

interface TemplateGroup {
  id: string;
  name: Record<string, string> | string;
  selectionType: string;
  minSelections: number;
  maxSelections?: number | null;
  sortOrder: number;
  options: TemplateOption[];
}

interface Template {
  id: string;
  name: Record<string, string> | string;
  scope: string;
  storefrontDisplayMode?: string | null;
  groups: TemplateGroup[];
}

interface TemplateCardProps {
  template: Template;
  onDelete?: (templateId: string) => void;
  deleting?: boolean;
}

export function TemplateCard({ template, onDelete, deleting }: TemplateCardProps) {
  const { t, i18n } = useTranslation("settings");
  const _currency = useSalonStore((s) => s.currentSalon?.currency ?? "USD");
  const isSystem = template.scope === "SYSTEM";

  const resolveText = (value: Record<string, string> | string | null | undefined): string => {
    if (!value) return "";
    if (typeof value === "string") return value;
    return getLocalizedText(value, i18n.language) ?? "";
  };

  const templateName = resolveText(template.name);

  const totalOptions = template.groups.reduce((sum, g) => sum + g.options.length, 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">{templateName}</CardTitle>
              {isSystem && (
                <Badge variant="secondary" className="text-xs">
                  {t("serviceTemplates.labels.system")}
                </Badge>
              )}
              {template.groups.length > 1 && (
                <Badge variant="outline" className="text-xs">
                  {template.groups.length} {t("serviceTemplates.labels.groups", "groups")}
                </Badge>
              )}
              {template.storefrontDisplayMode && (
                <Badge variant="outline" className="text-xs">
                  {template.storefrontDisplayMode === "TABLE"
                    ? t("serviceTemplates.displayMode.table", "Table")
                    : t("serviceTemplates.displayMode.regular", "Regular")}
                </Badge>
              )}
            </div>
          </div>

          {!isSystem && onDelete && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => onDelete(template.id)}
                  disabled={deleting}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("serviceTemplates.actions.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-3">
          {template.groups.map((group) => {
            const groupName = resolveText(group.name);
            return (
              <div key={group.id} className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{groupName}</span>
                  <Badge variant="outline" className="text-xs font-normal">
                    {group.selectionType === "SINGLE"
                      ? t("serviceTemplates.selectionTypes.single")
                      : t("serviceTemplates.selectionTypes.multiple")}
                  </Badge>
                </div>
                <div className="space-y-1">
                  {group.options.map((option) => {
                    const optionName = resolveText(option.optionName);
                    return (
                      <div
                        key={option.id}
                        className="flex items-center justify-between rounded-md border px-3 py-1.5 text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <span>{optionName}</span>
                          {option.isDefault && (
                            <Badge variant="outline" className="text-xs">
                              {t("serviceTemplates.labels.default")}
                            </Badge>
                          )}
                        </div>
                        {option.durationModifier !== 0 && (
                          <span className="text-xs text-muted-foreground">
                            {option.durationModifier > 0 ? "+" : ""}
                            {option.durationModifier}min
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {template.groups.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {t("serviceTemplates.messages.noGroups", "No option groups defined")}
            </p>
          )}

          <p className="text-xs text-muted-foreground">
            {template.groups.length} {t("serviceTemplates.labels.groupCount", "group(s)")},{" "}
            {totalOptions} {t("serviceTemplates.labels.optionCount", "option(s)")}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
